/**
 * Katalog App Root
 * 4-state navigation: LANDING, SIGNING UP, SIGNING IN, SIGNED IN
 *
 * MARKETINGBAR routes (guest): /, /auth, /landing
 * APP NAVBAR routes (signed-in): /home, /discover (signed-in), /create, /wishlist, /cart, /stash, /notifications, /account/*
 * AUTH-SPLIT routes (guest → MarketingBar, signed-in → App Navbar):
 *   /discover, /:handle, /:handle/store, /:handle/deals/:dealId, /:handle/:slug, /deal/:dealId
 *
 * /discover splits by auth: guest → MarketingLayout, signed-in → DashboardLayout
 * /create is app-only (redirects to /auth if guest)
 *
 * Forbidden routes removed: /stores, /katalogs, /katalog/:slug
 */
import React, { Suspense, lazy } from 'react';
import { Routes, Route, Navigate, useParams } from 'react-router-dom';

import { Spinner } from '@bright-web-skeleton/ui';
import { ProtectedRoute, useAuth } from '@bright-web-skeleton/auth';
import { ROUTES, routes } from './routes';
import { buildBrandStorePath } from '@bright-web-skeleton/shared/routing';
import { useInviteIQCapture } from '@bright-web-skeleton/shared/inviteiq';
import { AuthDrawerProvider } from './hooks/useAuthDrawer';
import { AuthDrawer } from './pages/components/AuthDrawer';
import { DashboardLayout } from './components/layout';
import { MarketingLayout } from './components/layout/MarketingLayout';

// Tailwind sanity page
const TailwindSanityPage = lazy(() =>
  import('@bright-web-skeleton/ui').then((m) => ({ default: (m as any).TailwindSanityPage })),
);

// ---- Public pages ----
const LandingPage = lazy(() =>
  import('./pages/LandingPage').then((m) => ({ default: m.LandingPage })),
);
const AuthPage = lazy(() => import('./pages/AuthPage').then((m) => ({ default: m.AuthPage })));
const ProfilePage = lazy(() =>
  import('./pages/ProfilePage').then((m) => ({ default: m.ProfilePage })),
);

// ---- URL System v1 pages ----
const HandleFeedPage = lazy(() =>
  import('./pages/HandleFeedPage').then((m) => ({ default: m.HandleFeedPage })),
);

const BrandStorePage = lazy(() =>
  import('./pages/BrandStorePage').then((m) => ({ default: m.BrandStorePage })),
);

// ---- Legacy redirects ----
const StoreRedirect = lazy(() =>
  import('./pages/StoreRedirect').then((m) => ({ default: m.StoreRedirect })),
);
const DealRedirect = lazy(() =>
  import('./pages/DealRedirect').then((m) => ({ default: m.DealRedirect })),
);

// ---- Customer authenticated pages ----
const CustomerHomePage = lazy(() =>
  import('./pages/customer/HomePage').then((m) => ({ default: m.HomePage })),
);

// Customer-target handle-scoped invite landing (cross-app, owned by Katalag).
const CustomerHandleInvitePage = lazy(() =>
  import('./pages/CustomerHandleInvitePage').then((m) => ({ default: m.CustomerHandleInvitePage })),
);
const CustomerDiscoverPage = lazy(() =>
  import('./pages/customer/DiscoverPage').then((m) => ({ default: m.DiscoverPage })),
);
const WishlistPage = lazy(() =>
  import('./pages/customer/WishlistPage').then((m) => ({ default: m.WishlistPage })),
);
const CartPage = lazy(() =>
  import('./pages/customer/CartPage').then((m) => ({ default: m.CartPage })),
);
const StashPage = lazy(() =>
  import('./pages/customer/StashPage').then((m) => ({ default: m.StashPage })),
);
const CustomerNotificationsPage = lazy(() =>
  import('./pages/customer/NotificationsPage').then((m) => ({ default: m.NotificationsPage })),
);
const CustomerProfilePage = lazy(() =>
  import('./pages/customer/ProfilePage').then((m) => ({ default: m.ProfilePage })),
);
const SecurityPage = lazy(() =>
  import('./pages/customer/SecurityPage').then((m) => ({ default: m.SecurityPage })),
);
const PreferencesPage = lazy(() =>
  import('./pages/customer/PreferencesPage').then((m) => ({ default: m.PreferencesPage })),
);
const CustomerDealDetailPage = lazy(() =>
  import('./pages/customer/DealDetailPage').then((m) => ({ default: m.DealDetailPage })),
);
const OrdersPage = lazy(() =>
  import('./pages/customer/OrdersPage').then((m) => ({ default: m.OrdersPage })),
);
const OrderDetailPage = lazy(() =>
  import('./pages/customer/OrderDetailPage').then((m) => ({ default: m.OrderDetailPage })),
);
const AddressBookPage = lazy(() =>
  import('./pages/customer/AddressBookPage').then((m) => ({ default: m.AddressBookPage })),
);
const OrderConfirmationPage = lazy(() =>
  import('./pages/customer/OrderConfirmationPage').then((m) => ({
    default: m.OrderConfirmationPage,
  })),
);
const ShopifyReturnPage = lazy(() =>
  import('./pages/customer/ShopifyReturnPage').then((m) => ({ default: m.ShopifyReturnPage })),
);
const AccountClaimPage = lazy(() =>
  import('./pages/customer/AccountClaimPage').then((m) => ({ default: m.AccountClaimPage })),
);
const OrderAddressPage = lazy(() =>
  import('./pages/customer/OrderAddressPage').then((m) => ({ default: m.OrderAddressPage })),
);
const CustomerAgentGatePage = lazy(() =>
  import('./pages/customer/CustomerAgentGatePage').then((m) => ({
    default: m.CustomerAgentGatePage,
  })),
);
const CreatorInsightsPage = lazy(() =>
  import('./pages/CreatorInsightsPage').then((m) => ({ default: m.CreatorInsightsPage })),
);
const KatalogInsightsPage = lazy(() =>
  import('./pages/KatalogInsightsPage').then((m) => ({ default: m.KatalogInsightsPage })),
);
const KatalogAccountPage = lazy(() =>
  import('./pages/account/KatalogAccountPage').then((m) => ({ default: m.KatalogAccountPage })),
);
const KatalogCreatePage = lazy(() =>
  import('./pages/account/KatalogCreatePage').then((m) => ({ default: m.KatalogCreatePage })),
);
const KatalogManagePage = lazy(() =>
  import('./pages/account/KatalogManagePage').then((m) => ({ default: m.KatalogManagePage })),
);
const KatalogManageRedirect = lazy(() =>
  import('./pages/account/KatalogManageRedirect').then((m) => ({
    default: m.KatalogManageRedirect,
  })),
);
const KatalogDashboardByIdPage = lazy(() =>
  import('./pages/account/KatalogDashboardByIdPage').then((m) => ({
    default: m.KatalogDashboardByIdPage,
  })),
);
const AdminDiscoverAuditPage = lazy(() =>
  import('./pages/admin/DiscoverAuditPage').then((m) => ({ default: m.DiscoverAuditPage })),
);

// ---- Extracted pages ----
const OnboardingPage = lazy(() =>
  import('./pages/OnboardingPage').then((m) => ({ default: m.OnboardingPage })),
);
const AuthCallbackPage = lazy(() =>
  import('./pages/AuthCallbackPage').then((m) => ({ default: m.AuthCallbackPage })),
);
const NotFoundPage = lazy(() =>
  import('./pages/NotFoundPage').then((m) => ({ default: m.NotFoundPage })),
);

// ---- Utility ----
const DemoTemplate = lazy(() =>
  import('./pages/demo-template').then((m) => ({ default: m.DemoTemplate })),
);
const SimpleTest = lazy(() =>
  import('./pages/SimpleTest').then((m) => ({ default: m.SimpleTest })),
);
const TokenChecker = lazy(() =>
  import('./pages/TokenChecker').then((m) => ({ default: m.TokenChecker })),
);

// ---- Suspense wrapper ----
const LazyPage: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Suspense
    fallback={
      <div className="min-h-[50vh] flex items-center justify-center">
        <Spinner size="lg" />
      </div>
    }
  >
    {children}
  </Suspense>
);

// ---- Protected route with DashboardLayout (APP NAVBAR) ----
const ProtectedWithLayout: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <ProtectedRoute redirectTo={ROUTES.AUTH} onboardingPath={ROUTES.ONBOARDING}>
    <DashboardLayout>
      <LazyPage>{children}</LazyPage>
    </DashboardLayout>
  </ProtectedRoute>
);

// ---- Legacy /:handle/store → /brand/:handle redirect ----
const HandleStoreRedirect: React.FC = () => {
  const { handle } = useParams<{ handle: string }>();
  return <Navigate to={handle ? buildBrandStorePath(handle) : ROUTES.HOME} replace />;
};

// ---- Root route guard: signed-in → /home, guest → Landing ----
const RootGuard: React.FC = () => {
  const { authUser, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Spinner size="lg" />
      </div>
    );
  }

  if (authUser) {
    return <Navigate to="/home" replace />;
  }

  return (
    <LazyPage>
      <LandingPage />
    </LazyPage>
  );
};

// ---- /discover guard: signed-in → DashboardLayout, guest → MarketingLayout ----
const DiscoverGuard: React.FC = () => {
  const { authUser, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Spinner size="lg" />
      </div>
    );
  }

  if (authUser) {
    return (
      <DashboardLayout>
        <LazyPage>
          <CustomerDiscoverPage />
        </LazyPage>
      </DashboardLayout>
    );
  }

  return (
    <MarketingLayout>
      <LazyPage>
        <CustomerDiscoverPage />
      </LazyPage>
    </MarketingLayout>
  );
};

// ---- Auth-aware layout guard: signed-in → DashboardLayout, guest → MarketingLayout ----
const AuthLayoutGuard: React.FC<{
  children: React.ReactNode;
  canonicalEntitySpace?: boolean;
}> = ({ children, canonicalEntitySpace = false }) => {
  const { authUser, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Spinner size="lg" />
      </div>
    );
  }

  if (authUser) {
    return (
      <DashboardLayout>
        <LazyPage>{children}</LazyPage>
      </DashboardLayout>
    );
  }

  return (
    <MarketingLayout canonicalEntitySpace={canonicalEntitySpace}>
      <LazyPage>{children}</LazyPage>
    </MarketingLayout>
  );
};

// ---- /create guard: signed-in → DashboardLayout, guest → redirect to /auth ----
const CreateGuard: React.FC = () => {
  const { authUser, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Spinner size="lg" />
      </div>
    );
  }

  if (!authUser) {
    return <Navigate to="/auth" replace />;
  }

  return (
    <DashboardLayout>
      <LazyPage>
        <div className="p-6 text-center text-muted-foreground">
          <h1 className="text-2xl font-bold text-foreground mb-2">Create</h1>
          <p>Create page coming soon.</p>
        </div>
      </LazyPage>
    </DashboardLayout>
  );
};

// ---- App Root ----
const App: React.FC = () => {
  useInviteIQCapture();

  return (
    <AuthDrawerProvider>
      <Routes>
        {/* ========== PUBLIC ROUTES (MARKETINGBAR) ========== */}

        {/* Root: signed-in → /home, guest → Landing */}
        <Route path={ROUTES.HOME} element={<RootGuard />} />
        <Route
          path={ROUTES.LANDING}
          element={
            <LazyPage>
              <LandingPage />
            </LazyPage>
          }
        />

        {/* Customer-facing token-validated address recovery (no auth required) */}
        <Route
          path="/order-address"
          element={
            <LazyPage>
              <OrderAddressPage />
            </LazyPage>
          }
        />

        {/* Auth pages — MARKETINGBAR */}
        <Route
          path={ROUTES.AUTH}
          element={
            <MarketingLayout>
              <LazyPage>
                <AuthPage />
              </LazyPage>
            </MarketingLayout>
          }
        />
        <Route
          path={ROUTES.AUTH_CALLBACK}
          element={
            <LazyPage>
              <AuthCallbackPage />
            </LazyPage>
          }
        />

        {/* Profile */}
        <Route
          path={ROUTES.PROFILE}
          element={
            <MarketingLayout>
              <LazyPage>
                <ProfilePage />
              </LazyPage>
            </MarketingLayout>
          }
        />

        {/* /discover — auth-split: signed-in → APP NAVBAR, guest → MARKETINGBAR */}
        <Route path={routes.app.discover} element={<DiscoverGuard />} />

        {/* /create — app-only, guest → /auth redirect */}
        <Route path="/create" element={<CreateGuard />} />

        {/* Bare deal route → redirect to canonical /:handle/deals/:dealId */}
        <Route
          path="/deal/:dealId"
          element={
            <LazyPage>
              <DealRedirect />
            </LazyPage>
          }
        />

        {/*
         * Customer-target handle-scoped invite pages (TARGET APP OWNS TARGET PAGE).
         * Mounted BEFORE the catch-all /:handle/:catalogSlug route so the static
         * /agent|/partner|/general-agent|/connector prefix wins. Each page resolves
         * the inviter via role_handles, mints/reuses an InviteIQ token, and
         * forwards into the canonical Katalag landing with `?iq=<token>`.
         */}
        <Route
          path="/agent/:handle/customer"
          element={
            <LazyPage>
              <CustomerHandleInvitePage inviterRole="agent" />
            </LazyPage>
          }
        />
        <Route
          path="/partner/:handle/customer"
          element={
            <LazyPage>
              <CustomerHandleInvitePage inviterRole="partner" />
            </LazyPage>
          }
        />
        <Route
          path="/general-agent/:handle/customer"
          element={
            <LazyPage>
              <CustomerHandleInvitePage inviterRole="general_agent" />
            </LazyPage>
          }
        />
        <Route
          path="/connector/:handle/customer"
          element={
            <LazyPage>
              <CustomerHandleInvitePage inviterRole="connector" />
            </LazyPage>
          }
        />

        {/* Canonical brand store routes */}
        <Route
          path={ROUTES.BRAND_CHILD_STORE}
          element={
            <AuthLayoutGuard canonicalEntitySpace>
              <BrandStorePage />
            </AuthLayoutGuard>
          }
        />
        <Route
          path={ROUTES.BRAND_STORE}
          element={
            <AuthLayoutGuard canonicalEntitySpace>
              <BrandStorePage />
            </AuthLayoutGuard>
          }
        />

        {/* Legacy redirects: /store/:slug → /brand/:slug */}
        <Route
          path={ROUTES.STORE_LEGACY}
          element={
            <LazyPage>
              <StoreRedirect />
            </LazyPage>
          }
        />

        {/* Utility */}
        <Route
          path={ROUTES.SIMPLE_TEST}
          element={
            <LazyPage>
              <SimpleTest />
            </LazyPage>
          }
        />
        <Route
          path={ROUTES.DEMO_TEMPLATE}
          element={
            <LazyPage>
              <DemoTemplate />
            </LazyPage>
          }
        />
        <Route
          path={ROUTES.TOKEN_CHECKER}
          element={
            <LazyPage>
              <TokenChecker />
            </LazyPage>
          }
        />
        <Route
          path={ROUTES.TAILWIND_SANITY}
          element={
            <LazyPage>
              <TailwindSanityPage />
            </LazyPage>
          }
        />

        {/* ========== ONBOARDING ========== */}
        <Route
          path={ROUTES.ONBOARDING}
          element={
            <ProtectedRoute redirectTo={ROUTES.AUTH} requireOnboarding={false}>
              <LazyPage>
                <OnboardingPage />
              </LazyPage>
            </ProtectedRoute>
          }
        />

        {/* ========== AUTHENTICATED ROUTES (APP NAVBAR via DashboardLayout) ========== */}
        <Route
          path={routes.app.home}
          element={
            <ProtectedWithLayout>
              <CustomerHomePage />
            </ProtectedWithLayout>
          }
        />
        <Route
          path={routes.app.wishlist}
          element={
            <ProtectedWithLayout>
              <WishlistPage />
            </ProtectedWithLayout>
          }
        />
        <Route
          path={routes.app.cart}
          element={
            <ProtectedWithLayout>
              <CartPage />
            </ProtectedWithLayout>
          }
        />
        <Route
          path={routes.app.stash}
          element={
            <ProtectedWithLayout>
              <StashPage />
            </ProtectedWithLayout>
          }
        />
        <Route
          path={routes.app.notifications}
          element={
            <ProtectedWithLayout>
              <CustomerNotificationsPage />
            </ProtectedWithLayout>
          }
        />
        {/* Authenticated bare deal route also redirects to canonical handle URL */}
        <Route
          path={routes.app.dealDetail}
          element={
            <ProtectedWithLayout>
              <DealRedirect />
            </ProtectedWithLayout>
          }
        />
        {/*
         * Order confirmation MUST be reachable without ProtectedRoute so the
         * Stripe redirect (which can land before the auth session rehydrates,
         * or in a fresh browser context) does not bounce to /auth → /home.
         * Authorization is enforced by the get_order_confirmation RPC, which
         * accepts either the authenticated owner or a matching session_id.
         */}
        <Route
          path={routes.app.orderConfirmation}
          element={
            <DashboardLayout>
              <LazyPage>
                <OrderConfirmationPage />
              </LazyPage>
            </DashboardLayout>
          }
        />
        {/*
         * Shopify "Return to Katalag" — Phase 2.2 (staging).
         * Public route by design: the buyer arrives directly from Shopify
         * Checkout via the (future) Checkout UI extension. The page calls
         * /api/shopify-return/verify and only renders generic state; the
         * canonical /order-confirmation page handles all sensitive detail
         * with its own access checks.
         */}
        <Route
          path="/shopify/return"
          element={
            <LazyPage>
              <ShopifyReturnPage />
            </LazyPage>
          }
        />
        {/* Phase 20G.1a.3 — opaque-token account-claim flow for Shopify
            guest buyers who consented to a Katalog account invitation. */}
        <Route
          path="/account/claim"
          element={
            <LazyPage>
              <AccountClaimPage />
            </LazyPage>
          }
        />

        {/* Account */}
        <Route
          path={routes.account.orders}
          element={
            <ProtectedWithLayout>
              <OrdersPage />
            </ProtectedWithLayout>
          }
        />
        <Route
          path={routes.account.orderDetail}
          element={
            <ProtectedWithLayout>
              <OrderDetailPage />
            </ProtectedWithLayout>
          }
        />
        <Route
          path={routes.account.addresses}
          element={
            <ProtectedWithLayout>
              <AddressBookPage />
            </ProtectedWithLayout>
          }
        />
        <Route
          path={routes.account.profile}
          element={
            <ProtectedWithLayout>
              <CustomerProfilePage />
            </ProtectedWithLayout>
          }
        />
        <Route
          path={routes.account.security}
          element={
            <ProtectedWithLayout>
              <SecurityPage />
            </ProtectedWithLayout>
          }
        />
        <Route
          path={routes.account.preferences}
          element={
            <ProtectedWithLayout>
              <PreferencesPage />
            </ProtectedWithLayout>
          }
        />
        <Route path="/account" element={<Navigate to={routes.account.profile} replace />} />
        <Route
          path="/account/become-agent"
          element={
            <ProtectedWithLayout>
              <CustomerAgentGatePage />
            </ProtectedWithLayout>
          }
        />
        <Route
          path="/account/insights"
          element={
            <ProtectedWithLayout>
              <CreatorInsightsPage />
            </ProtectedWithLayout>
          }
        />
        <Route
          path="/account/katalog-insights"
          element={
            <ProtectedWithLayout>
              <KatalogInsightsPage />
            </ProtectedWithLayout>
          }
        />
        <Route
          path="/account/katalog"
          element={
            <ProtectedWithLayout>
              <KatalogAccountPage />
            </ProtectedWithLayout>
          }
        />
        <Route
          path="/account/katalog/create"
          element={
            <ProtectedWithLayout>
              <KatalogCreatePage />
            </ProtectedWithLayout>
          }
        />
        <Route
          path="/account/katalog/manage"
          element={
            <ProtectedWithLayout>
              <KatalogManageRedirect />
            </ProtectedWithLayout>
          }
        />
        <Route
          path="/account/katalog/:id/dashboard"
          element={
            <ProtectedWithLayout>
              <KatalogDashboardByIdPage />
            </ProtectedWithLayout>
          }
        />
        <Route
          path="/account/katalog/:id/manage"
          element={
            <ProtectedWithLayout>
              <KatalogManagePage />
            </ProtectedWithLayout>
          }
        />
        <Route
          path="/account/admin/discover-audit"
          element={
            <ProtectedWithLayout>
              <AdminDiscoverAuditPage />
            </ProtectedWithLayout>
          }
        />

        {/* ========== HANDLE-BASED ROUTES (auth-split layout, must be last — catch-all) ========== */}
        {/* Handle deal routes → DealDetailPage with creator context (most specific first) */}
        <Route
          path="/:handle/:slug/deal/:dealId"
          element={
            <AuthLayoutGuard>
              <CustomerDealDetailPage />
            </AuthLayoutGuard>
          }
        />
        {/* Legacy /:handle/store → /brand/:handle redirect */}
        <Route path={ROUTES.HANDLE_STORE_LEGACY} element={<HandleStoreRedirect />} />
        <Route
          path={ROUTES.HANDLE_DEAL}
          element={
            <AuthLayoutGuard>
              <CustomerDealDetailPage />
            </AuthLayoutGuard>
          }
        />
        {/* Handle feed routes */}
        <Route
          path={ROUTES.HANDLE_CATALOG}
          element={
            <AuthLayoutGuard canonicalEntitySpace>
              <HandleFeedPage />
            </AuthLayoutGuard>
          }
        />
        <Route
          path={ROUTES.HANDLE_HUB}
          element={
            <AuthLayoutGuard canonicalEntitySpace>
              <HandleFeedPage />
            </AuthLayoutGuard>
          }
        />

        {/* 404 */}
        <Route
          path="*"
          element={
            <MarketingLayout>
              <LazyPage>
                <NotFoundPage />
              </LazyPage>
            </MarketingLayout>
          }
        />
      </Routes>
      <AuthDrawer />
    </AuthDrawerProvider>
  );
};

export default App;
